import { useState, useEffect, useRef } from 'react';
import './style.scss';
import DataUrlHelper from '../../../../helpers/data-url.helper';
import checkCircleGreenIcon from '../../../../../assets/icons/check-circle-green-outline.svg';
import infoCircleBlueIcon from '../../../../../assets/icons/info-circle-blue-outline.svg';
import exclamationHexagonRedIcon from '../../../../../assets/icons/exclamation-hexagon-red-outline.svg';
import exclamationCircleRedIcon from '../../../../../assets/icons/exclamation-circle-red.svg';
import { InformationBoxType } from './information-box-type.enum';

export interface InformationBoxContent {
  type: InformationBoxType;
  headingElement?: React.ReactNode;
  textElement?: React.ReactNode;
  className?: string;
  closable?: boolean;
  setFocus?: boolean;
  closeCallback?: () => void;
}

function getCssClasses(type: InformationBoxType): string {
  switch (type) {
    case InformationBoxType.Success:
      return 'information-box--success bg-blue border border-blue';
    case InformationBoxType.Danger:
      return 'information-box--danger bg-pink';
    case InformationBoxType.Error:
      return 'information-box--error border rounded border-red';
    default:
      return 'information-box--default bg-blue';
  }
}

function InformationBox({ type = InformationBoxType.Default, headingElement, textElement, className, closable = true, setFocus = false, closeCallback }: InformationBoxContent): React.ReactElement {
  const [isVisible, setIsVisible] = useState(true);
  // Only want to fade-in infomration box if it is of type success, or in the future danger.
  const [fadeIn, setFadeIn] = useState(type !== InformationBoxType.Success);
  const informationBoxRef = useRef<HTMLDivElement>(null);

  // Only want to fade-in infomration box if it is of type success, or in the future danger.
  useEffect(() => {
    if (type === InformationBoxType.Success) {
      setTimeout(() => {
        setFadeIn(true);
        if (setFocus && informationBoxRef.current) {
          informationBoxRef.current.focus();
        }
      }, 100);
    }
  }, [type, setFocus]);

  function handleClose() {
    closeCallback?.();
    setIsVisible(false);
  }

  if (!isVisible) {
    return <></>;
  }

  function renderByType() {
    switch (type) {
      case InformationBoxType.Success:
        return (
          <>
            <div className={`d-flex align-items-center information-box__header-wrapper ${!textElement ? 'information-box__header-wrapper--no-margin-bottom' : ''}`}>
              <div className="d-flex flex-grow-1 align-items-center">
                <img alt="" className="information-box__header-icon me-2" src={DataUrlHelper.encode(checkCircleGreenIcon)} />
                {headingElement && <div className="flex-grow-1 information-box__heading">{headingElement}</div>}
              </div>
              {closable && <button type="button" className="btn-close" aria-label="Stäng" onClick={handleClose}></button>}
            </div>
            {textElement && <div className="flex-grow-1">{textElement}</div>}
          </>
        );
      case InformationBoxType.Default:
        return (
          <>
            {headingElement && <>{headingElement}</>}
            {textElement && <div>{textElement}</div>}
          </>
        );
      case InformationBoxType.Information:
        return (
          <>
            {headingElement && <>{headingElement}</>}
            <div className="d-flex flex-grow-1 align-items-center">
              <img alt="" className="align-self-start me-2" src={DataUrlHelper.encode(infoCircleBlueIcon)} />
              {textElement && <div className="flex-grow-1">{textElement}</div>}
            </div>
          </>
        );
      case InformationBoxType.Danger:
        return (
          <>
            <div className={`d-flex align-items-center information-box__header-wrapper ${!textElement ? 'information-box__header-wrapper--no-margin-bottom' : ''}`}>
              <div className="d-flex flex-grow-1 align-items-center">
                <img alt="" className="information-box__header-icon me-2" src={DataUrlHelper.encode(exclamationHexagonRedIcon)} />
                {headingElement && <div className="flex-grow-1 information-box__heading">{headingElement}</div>}
              </div>
            </div>
            {textElement && <div>{textElement}</div>}
          </>
        );
      case InformationBoxType.Error:
        return (
          <>
            <div className={`d-flex align-items-center information-box__header-wrapper ${!textElement ? 'information-box__header-wrapper--no-margin-bottom' : ''}`}>
              {headingElement && (
                <div className={`d-flex flex-grow-1 align-items-center ${textElement ? 'mb-2' : ''}`}>
                  <img alt="" className="information-box__header-icon me-2" src={DataUrlHelper.encode(exclamationCircleRedIcon)} />
                  <div className="flex-grow-1 information-box__heading">{headingElement}</div>
                </div>
              )}
            </div>
            {textElement && <div>{textElement}</div>}
          </>
        );
      default:
        return null;
    }
  }

  return (
    <div
      ref={informationBoxRef}
      className={`information-box ${getCssClasses(type)} p-3 p-sm-4 ${className ?? ''} information-box--fade-in ${fadeIn ? 'information-box--fade-in--show' : ''}`}
      tabIndex={setFocus ? -1 : undefined}
    >
      {renderByType()}
    </div>
  );
}

export default InformationBox;
